import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { SinglePost } from '~/components/business/SinglePost'
import { GlobalRequest } from '~/components/global-request'
import LayoutSwitch from '~/components/ui/LayoutSwitch'
import Loader from '~/components/pages/home/Loader'
import Sort from '~/components/pages/home/Sort'
import Tabs from '~/components/pages/home/Tabs'
import BannerList from '~/components/pages/home/BannerList'
import Search from '~/components/pages/home/Search'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { PAGE_SIZE } from '~/configs'
import { GET_TABS, SEARCH } from '~/graphqls/gqls'
import {
  CopyrightAiTextSearchParam,
  getTabs,
  getTabs_aiArtworkResourceModule_aiSquareTab,
  PostTypes,
  search,
  searchVariables,
} from '~/graphqls/types'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import useLayoutState from '~/hooks/useLayoutState'
import { Layout } from '~/layouts/default'
import {
  CacheValueProps,
  getGraphData,
  getScrollCache,
  setScrollCache,
} from '~/utils'
import {
  getSearchParams,
  isSearchParamsSame,
  showSearchResultCount,
} from '~/utils/search'
import { SEOHead } from '~/components/business/SEOHead'
import useScrollTop from '~/hooks/useScrollTop'
import { useMasonry } from '~/hooks/useMasonry'
import useInfiniteScrollCursor from '~/hooks/useInfiniteScrollCursor'
import UpdateEngine from '~/components/business/SinglePost/UpdateEngine'
import { AdvDialogModal } from '~/components/pages/dialog/AdvDialogModal'

interface Props {
  tabs: getTabs_aiArtworkResourceModule_aiSquareTab
  resultList: PostTypes[]
  hasNextPage: boolean
  total: number
  scrollCache: CacheValueProps
  endCursor: string
}

const Home = ({
  tabs,
  resultList,
  hasNextPage: _hasNextPage,
  total: _total,
  scrollCache,
  endCursor: _endCursor,
}: Props) => {
  const scrollTop = useScrollTop()
  const router = useRouter()
  const [colCount, setColCount] = useLayoutState('homepage-layout', '6')
  const [loading, setLoading] = useState(false)

  const [extraQueryParams, setExtraQueryParams] = useState<
    Partial<CopyrightAiTextSearchParam>
  >(getSearchParams(router.query, tabs))

  const [initList, setInitList] = useState(resultList)
  const [hasNextPage, setHasNextPage] = useState(_hasNextPage)
  const [total, setTotal] = useState(_total)
  const [endCursor, setEndCursor] = useState(_endCursor)

  const { InfiniteScollComp, list } = useInfiniteScrollCursor<
    PostTypes,
    search,
    searchVariables
  >({
    queryDoc: SEARCH,
    initData: initList,
    initPageSize: PAGE_SIZE,
    hasNextPage: hasNextPage,
    needCompleteData: false,
    endCursor: endCursor,
    extraQueryParams: {
      param: {
        ...(extraQueryParams as CopyrightAiTextSearchParam),
      },
    },
    hasNextPageExtractor: res => {
      return (
        res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.hasNextPage || false
      )
    },
    endCursorExtractor: res => {
      return (
        res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo?.endCursor ||
        ''
      )
    },
    dataExtractor: res => {
      return getGraphData<PostTypes>(
        res.data.aiArtworkResourceModule?.wjAiTextSearch?.edges || []
      )
    },
  })

  const { columnList, columnWidth, columnGutter } = useMasonry<PostTypes>({
    data: list,
    sizeLoader: (it: PostTypes) => {
      if (!it?.coverImage?.height || !it?.coverImage?.width) return 1
      return it?.coverImage?.height / it?.coverImage?.width
    },
    column: Number(colCount),
    itemExtraHeight: 56,
  })

  const searchAction = useImperativeQuery<search, searchVariables>(SEARCH)
  useDidUpdateEffect(() => {
    setLoading(true)
    searchAction({
      first: PAGE_SIZE,
      param: {
        ...(extraQueryParams as CopyrightAiTextSearchParam),
      },
    })
      .then(res => {
        setInitList(
          getGraphData(
            res.data.aiArtworkResourceModule?.wjAiTextSearch?.edges || []
          )
        )
        setHasNextPage(
          res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
            ?.hasNextPage || false
        )
        setTotal(
          res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
            ?.totalCount || 0
        )
        setEndCursor(
          res.data.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
            ?.endCursor || ''
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [extraQueryParams])

  useDidUpdateEffect(() => {
    const params = getSearchParams(router.query, tabs)
    if (isSearchParamsSame(params, extraQueryParams as any)) {
      return
    }
    setExtraQueryParams(params)
  }, [router.asPath])

  useEffect(() => {
    window.scrollTo({ top: scrollCache.scrollTop || 0, behavior: 'auto' })
  }, [])

  useDidUpdateEffect(() => {
    setScrollCache({
      key: router.asPath,
      value: {
        path: router.asPath,
        scrollTop,
        pageSize: list.length,
      },
    })
  }, [list.length, scrollTop, router.asPath])

  return (
    <Layout headerType="no-margin">
      <SEOHead />
      <BannerList>
        <Search />
      </BannerList>

      <MyContainer>
        <Tabs tabs={tabs} />

        <div className="flex items-center justify-between pt-[18px] pb-4">
          <Sort model={extraQueryParams.model} />
          <div className="flex items-center text-xs">
            {/* 有数据，且keyword为空 */}
            {total !== 0 && showSearchResultCount(extraQueryParams as any) && (
              <div className="mr-3">
                共找到<span className="text-primary">{total}</span>条结果
              </div>
            )}
            <LayoutSwitch col={colCount} setCol={setColCount} />
          </div>
        </div>

        <section>
          <div className="mb-8">
            <Loader
              cols={Number(colCount)}
              rows={4}
              containerWidth={1392}
              loading={loading}
            >
              {!loading && (
                <>
                  {list.length ? (
                    <>
                      <div className="flex justify-between items-start">
                        {columnList?.map((item, index) => (
                          <div
                            style={{
                              width: columnWidth,
                            }}
                            key={index}
                          >
                            {item?.map((it, i) => (
                              <div
                                key={it.id}
                                style={{
                                  marginBottom: columnGutter,
                                }}
                              >
                                <SinglePost post={it} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                      {InfiniteScollComp}
                    </>
                  ) : (
                    <MyEmpty />
                  )}
                </>
              )}
            </Loader>
          </div>
        </section>
      </MyContainer>

      {/* 一键同款的轮询状态 */}
      <UpdateEngine />
      {/** 首页弹窗广告 */}
      <AdvDialogModal />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)

  const scrollCache = getScrollCache(context)

  const tabRes = await client.query<getTabs>({
    query: GET_TABS,
    fetchPolicy: 'no-cache',
  })

  if (!tabRes.data.aiArtworkResourceModule?.aiSquareTab) {
    throw new Error('failed to fetch tab data')
  }

  const params = getSearchParams(
    context.query,
    tabRes.data.aiArtworkResourceModule?.aiSquareTab
  )

  const [resultRes] = await Promise.all([
    client.query<search, searchVariables>({
      query: SEARCH,
      variables: {
        first: scrollCache.pageSize || PAGE_SIZE,
        param: params,
      },
      fetchPolicy: 'no-cache',
    }),
  ])

  return {
    props: {
      initialState,
      scrollCache,
      tabs: tabRes.data.aiArtworkResourceModule?.aiSquareTab,
      resultList: getGraphData(
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.edges || []
      ),
      hasNextPage:
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.hasNextPage || false,
      total:
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.totalCount || 0,
      endCursor:
        resultRes?.data?.aiArtworkResourceModule?.wjAiTextSearch?.pageInfo
          ?.endCursor,
    },
  }
}

export default Home
